import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Users } from 'lucide-react';
import { AppShell } from '@/components/layout/AppShell';
import { Card, CardHeader, CardBody } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Spinner, EmptyState } from '@/components/ui/EmptyState';
import { supabase } from '@/lib/supabaseClient';
import { rxkModuleIcon, rxkModuleTitle, rxkNavGroups } from '../../nav';

interface DiscountDetail {
  code: string | null;
  label: string;
  type: 'percent' | 'fixed';
  value: number;
  applies_to: 'all' | 'specific';
  skus: string[];
  min_order_amount: number | null;
  starts_at: string | null;
  ends_at: string | null;
  max_uses: number | null;
  used_count: number;
  status: 'active' | 'inactive';
}

interface RedemptionRow {
  id: string;
  customer_name: string;
  order_no: string | null;
  discount_amount_bdt: number;
  redeemed_at: string;
}

// View-only, for every rxk role including admin — discounts affect real
// money at checkout, so there is exactly one place they can be created or
// changed: ERP's own Discounts page (erp-thekseoul/src/pages/core/
// discounts/page.tsx). This page just shows what's live and who used it.
export default function AdminDiscountDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [discount, setDiscount] = useState<DiscountDetail | null>(null);
  const [redemptions, setRedemptions] = useState<RedemptionRow[]>([]);

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    const [discRes, redRes] = await Promise.all([
      supabase
        .from('rxk_discounts')
        .select('code, label, type, value, applies_to, skus, min_order_amount, starts_at, ends_at, max_uses, used_count, status')
        .eq('id', id)
        .maybeSingle(),
      supabase
        .from('rxk_discount_redemptions')
        .select('id, customer_name, order_no, discount_amount_bdt, redeemed_at')
        .eq('discount_id', id)
        .order('redeemed_at', { ascending: false }),
    ]);
    if (!discRes.data) {
      setNotFound(true);
      setLoading(false);
      return;
    }
    setDiscount(discRes.data as DiscountDetail);
    setRedemptions((redRes.data as RedemptionRow[]) ?? []);
    setLoading(false);
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) {
    return (
      <AppShell moduleTitle={rxkModuleTitle} moduleIcon={rxkModuleIcon} navGroups={rxkNavGroups}>
        <Spinner />
      </AppShell>
    );
  }

  if (notFound || !discount) {
    return (
      <AppShell moduleTitle={rxkModuleTitle} moduleIcon={rxkModuleIcon} navGroups={rxkNavGroups}>
        <EmptyState title="Discount not found" />
      </AppShell>
    );
  }

  return (
    <AppShell moduleTitle={rxkModuleTitle} moduleIcon={rxkModuleIcon} navGroups={rxkNavGroups}>
      <div className="space-y-5">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/admin/discounts')} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-surface-muted text-ink-faint cursor-pointer" aria-label="Back">
            <ArrowLeft className="w-4 h-4" />
          </button>
          <h1 className="text-[20px] font-bold text-ink flex-1 truncate">{discount.label || 'Untitled Discount'}</h1>
          <Badge tone={discount.status === 'active' ? 'success' : 'neutral'}>{discount.status}</Badge>
          <Badge tone="neutral">View only — edit in ERP</Badge>
        </div>

        <Card>
          <CardHeader title="Discount Settings" />
          <CardBody className="space-y-3 text-[13px]">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <p className="text-[11px] text-ink-faint">Coupon code</p>
                <p className="text-ink font-medium">{discount.code || 'Automatic (no code)'}</p>
              </div>
              <div>
                <p className="text-[11px] text-ink-faint">Value</p>
                <p className="text-ink font-medium">{discount.type === 'percent' ? `${discount.value}%` : `৳${Number(discount.value).toLocaleString()}`} off</p>
              </div>
              <div>
                <p className="text-[11px] text-ink-faint">Applies to</p>
                <p className="text-ink font-medium">{discount.applies_to === 'all' ? 'All products' : 'Specific SKUs'}</p>
              </div>
              <div>
                <p className="text-[11px] text-ink-faint">Minimum order amount</p>
                <p className="text-ink font-medium">{discount.min_order_amount != null ? `৳${Number(discount.min_order_amount).toLocaleString()}` : '—'}</p>
              </div>
              {discount.applies_to === 'specific' && (
                <div className="sm:col-span-2">
                  <p className="text-[11px] text-ink-faint">SKUs</p>
                  <p className="text-ink font-medium whitespace-pre-line">{discount.skus.join('\n') || '—'}</p>
                </div>
              )}
              <div>
                <p className="text-[11px] text-ink-faint">Starts at</p>
                <p className="text-ink font-medium">{discount.starts_at ? new Date(discount.starts_at).toLocaleString() : '—'}</p>
              </div>
              <div>
                <p className="text-[11px] text-ink-faint">Ends at</p>
                <p className="text-ink font-medium">{discount.ends_at ? new Date(discount.ends_at).toLocaleString() : '—'}</p>
              </div>
              <div>
                <p className="text-[11px] text-ink-faint">Max uses</p>
                <p className="text-ink font-medium">{discount.max_uses ?? 'Unlimited'}</p>
              </div>
            </div>
            <p className="text-[11px] text-ink-faint pt-1">Used {discount.used_count} time{discount.used_count === 1 ? '' : 's'} so far.</p>
          </CardBody>
        </Card>

        <Card>
          <CardHeader title="Who used this discount" subtitle={`${redemptions.length} redemption(s)`} icon={<Users className="w-4 h-4" />} />
          <CardBody className="space-y-1">
            {redemptions.length === 0 ? (
              <EmptyState title="Not used yet" />
            ) : (
              redemptions.map((r) => (
                <div key={r.id} className="flex items-center justify-between gap-3 px-2 py-2 rounded-lg">
                  <div className="min-w-0">
                    <p className="text-[12.5px] font-semibold text-ink truncate">{r.customer_name}</p>
                    <p className="text-[11px] text-ink-faint">{r.order_no ?? '—'} · {r.redeemed_at.slice(0, 10)}</p>
                  </div>
                  <span className="text-[12.5px] font-medium text-ink flex-shrink-0">-৳{Number(r.discount_amount_bdt).toLocaleString()}</span>
                </div>
              ))
            )}
          </CardBody>
        </Card>
      </div>
    </AppShell>
  );
}
